import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import {
  Database,
  FileText,
  Scale,
  AlertTriangle,
  Book,
  FileCheck,
  Mail,
  FlaskConical,
  Sparkles,
  History,
  Search,
  Filter,
  Plus,
  Edit,
  Eye,
  Copy,
  Power,
  ChevronRight,
  RefreshCw,
  Download,
  Upload,
  Check,
  X,
  Info,
  ExternalLink,
  Loader2,
  LayoutGrid,
  List,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Skeleton } from '../../components/ui/skeleton';
import { Badge } from '../../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '../../components/ui/sheet';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';

// Normalize backend URL to prevent double /api paths
const rawBackendUrl = (process.env.REACT_APP_BACKEND_URL || '').trim().replace(/\/$/, '');
const BACKEND_URL = rawBackendUrl.replace(/\/api$/, '');
const API = `${BACKEND_URL}/api/admin/metro2-intelligence`;

// Tab configuration
const TABS = [
  { id: 'overview', label: 'Overview', icon: LayoutGrid },
  { id: 'rules', label: 'Rule Library', icon: Database },
  { id: 'field-mappings', label: 'Field Codes', icon: FileText },
  { id: 'legal-authorities', label: 'Legal Authorities', icon: Scale },
  { id: 'red-flags', label: 'Red Flags', icon: AlertTriangle },
  { id: 'playbooks', label: 'Playbooks', icon: Book },
  { id: 'evidence', label: 'Evidence', icon: FileCheck },
  { id: 'strategies', label: 'Letter Strategies', icon: Mail },
  { id: 'testing', label: 'Rule Testing', icon: FlaskConical },
  { id: 'ai-assistant', label: 'AI Assistant', icon: Sparkles },
  { id: 'audit', label: 'Audit History', icon: History },
];

const SEVERITY_COLORS = {
  critical: 'bg-red-500/10 text-red-400 border-red-500/30',
  high: 'bg-orange-500/10 text-orange-400 border-orange-500/30',
  medium: 'bg-amber-500/10 text-amber-400 border-amber-500/30',
  low: 'bg-slate-500/10 text-slate-400 border-slate-500/30',
};

const STATUS_COLORS = {
  active: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30',
  disabled: 'bg-slate-500/10 text-slate-400 border-slate-500/30',
  draft: 'bg-amber-500/10 text-amber-400 border-amber-500/30',
  archived: 'bg-slate-600/10 text-slate-500 border-slate-600/30',
  approved: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30',
  pending_review: 'bg-amber-500/10 text-amber-400 border-amber-500/30',
  rejected: 'bg-red-500/10 text-red-400 border-red-500/30',
};

// ============================================================================
// OVERVIEW TAB
// ============================================================================
const OverviewTab = ({ overview, loading, onRefresh }) => {
  if (loading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[...Array(8)].map((_, i) => (
            <Skeleton key={i} className="h-24 rounded-xl bg-stone-800" />
          ))}
        </div>
      </div>
    );
  }

  const counts = overview?.counts || {};

  const cards = [
    { label: 'Active Rules', value: counts.active_rules || 0, icon: Database, color: 'emerald' },
    { label: 'Legal Authorities', value: counts.legal_authorities || 0, icon: Scale, color: 'blue' },
    { label: 'Field Mappings', value: counts.field_mappings || 0, icon: FileText, color: 'purple' },
    { label: 'Red Flags', value: counts.red_flags || 0, icon: AlertTriangle, color: 'red' },
    { label: 'Playbooks', value: counts.playbooks || 0, icon: Book, color: 'amber' },
    { label: 'Evidence Requirements', value: counts.evidence_requirements || 0, icon: FileCheck, color: 'teal' },
    { label: 'Letter Strategies', value: counts.letter_strategies || 0, icon: Mail, color: 'indigo' },
    { label: 'Rules Needing Review', value: counts.rules_needing_review || 0, icon: AlertTriangle, color: 'orange' },
  ];

  const colorClasses = {
    emerald: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30',
    blue: 'bg-blue-500/10 text-blue-400 border-blue-500/30',
    purple: 'bg-purple-500/10 text-purple-400 border-purple-500/30',
    red: 'bg-red-500/10 text-red-400 border-red-500/30',
    amber: 'bg-amber-500/10 text-amber-400 border-amber-500/30',
    teal: 'bg-teal-500/10 text-teal-400 border-teal-500/30',
    indigo: 'bg-indigo-500/10 text-indigo-400 border-indigo-500/30',
    orange: 'bg-orange-500/10 text-orange-400 border-orange-500/30',
  };

  return (
    <div className="space-y-6" data-testid="metro2-overview">
      {/* Canonical Info Banner */}
      <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-4 flex items-center gap-4">
        <div className="w-12 h-12 rounded-lg bg-emerald-500/20 flex items-center justify-center">
          <Info className="text-emerald-400" size={24} />
        </div>
        <div>
          <h3 className="font-semibold text-emerald-300">Metro 2 Technical Flow</h3>
          <p className="text-sm text-emerald-400/70">
            Canonical Method: <code className="bg-emerald-500/20 px-2 py-0.5 rounded">metro2_technical</code> | 
            Family: <code className="bg-emerald-500/20 px-2 py-0.5 rounded ml-1">metro2</code>
          </p>
        </div>
        <Button 
          variant="outline" 
          size="sm" 
          onClick={onRefresh}
          className="ml-auto border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10"
        >
          <RefreshCw size={14} className="mr-2" /> Refresh
        </Button>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {cards.map((card, i) => (
          <div
            key={i}
            className={`rounded-xl border p-5 ${colorClasses[card.color]}`}
            data-testid={`overview-card-${card.label.toLowerCase().replace(/\s+/g, '-')}`}
          >
            <div className="flex items-center justify-between mb-3">
              <card.icon size={20} />
              <span className="text-2xl font-bold">{card.value}</span>
            </div>
            <p className="text-sm opacity-80">{card.label}</p>
          </div>
        ))}
      </div>

      {/* Safe Language Note */}
      <div className="bg-amber-500/5 border border-amber-500/20 rounded-xl p-4">
        <h4 className="font-semibold text-amber-300 mb-2 flex items-center gap-2">
          <AlertTriangle size={16} /> Important Compliance Note
        </h4>
        <p className="text-sm text-amber-400/80">
          Metro 2 is the industry furnishing format used to structure reported account data. 
          While Metro 2 is not itself a statute, Metro 2 field inconsistencies may support a dispute 
          under the FCRA and Regulation V because the reporting may be inaccurate, incomplete, 
          unverifiable, or misleading.
        </p>
      </div>
    </div>
  );
};

// ============================================================================
// RULES TAB
// ============================================================================
const RulesTab = ({ rules, loading, onViewRule, onRefresh }) => {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const filteredRules = rules.filter(rule => {
    const matchesSearch = !search || 
      rule.rule_name?.toLowerCase().includes(search.toLowerCase()) ||
      rule.rule_key?.toLowerCase().includes(search.toLowerCase());
    const matchesStatus = statusFilter === 'all' || rule.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  if (loading) {
    return (
      <div className="space-y-4">
        {[...Array(5)].map((_, i) => (
          <Skeleton key={i} className="h-20 rounded-xl bg-stone-800" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="metro2-rules-tab">
      {/* Filters */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={16} />
          <Input
            placeholder="Search rules..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10 bg-stone-900 border-stone-800"
            data-testid="rules-search"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-40 bg-stone-900 border-stone-800" data-testid="rules-status-filter">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="disabled">Disabled</SelectItem>
            <SelectItem value="draft">Draft</SelectItem>
          </SelectContent>
        </Select>
        <Button variant="outline" size="icon" onClick={onRefresh} className="border-stone-700">
          <RefreshCw size={16} />
        </Button>
      </div>

      {/* Rules List */}
      <div className="space-y-3">
        {filteredRules.length === 0 ? (
          <div className="text-center py-12 text-slate-500">
            <Database size={48} className="mx-auto mb-4 opacity-30" />
            <p>No rules found</p>
          </div>
        ) : (
          filteredRules.map(rule => (
            <div
              key={rule.id}
              className="bg-stone-900 border border-stone-800 rounded-xl p-4 hover:border-emerald-500/30 transition-all cursor-pointer"
              onClick={() => onViewRule(rule)}
              data-testid={`rule-card-${rule.rule_key}`}
            >
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-emerald-600/20 flex items-center justify-center">
                    <Database className="text-emerald-400" size={18} />
                  </div>
                  <div>
                    <h4 className="font-semibold text-white">{rule.rule_name}</h4>
                    <p className="text-xs text-slate-500 font-mono">{rule.rule_key}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className={STATUS_COLORS[rule.status]}>
                    {rule.status}
                  </Badge>
                  <Badge variant="outline" className={SEVERITY_COLORS[rule.severity]}>
                    {rule.severity}
                  </Badge>
                  <ChevronRight size={16} className="text-slate-600" />
                </div>
              </div>
              {rule.description && (
                <p className="text-sm text-slate-400 mt-2 line-clamp-2">{rule.description}</p>
              )}
              <div className="flex items-center gap-4 mt-3 text-xs text-slate-500">
                {rule.metro2_codes?.length > 0 && (
                  <span className="flex items-center gap-1">
                    <FileText size={12} /> {rule.metro2_codes.join(', ')}
                  </span>
                )}
                <span className="flex items-center gap-1">
                  <Info size={12} /> {rule.eligibility_status?.replace(/_/g, ' ')}
                </span>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

// ============================================================================
// FIELD MAPPINGS TAB
// ============================================================================
const FieldMappingsTab = ({ mappings, loading }) => {
  const [search, setSearch] = useState('');
  const [groupFilter, setGroupFilter] = useState('all');

  // Get unique groups
  const groups = [...new Set(mappings.map(m => m.field_group))].filter(Boolean);

  const filteredMappings = mappings.filter(m => {
    const matchesSearch = !search || 
      m.display_name?.toLowerCase().includes(search.toLowerCase()) ||
      m.metro2_code_or_segment?.toLowerCase().includes(search.toLowerCase());
    const matchesGroup = groupFilter === 'all' || m.field_group === groupFilter;
    return matchesSearch && matchesGroup;
  });

  // Group by field_group
  const groupedMappings = filteredMappings.reduce((acc, m) => {
    const group = m.field_group || 'other';
    if (!acc[group]) acc[group] = [];
    acc[group].push(m);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="space-y-4">
        {[...Array(4)].map((_, i) => (
          <Skeleton key={i} className="h-32 rounded-xl bg-stone-800" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="metro2-field-mappings-tab">
      {/* Filters */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={16} />
          <Input
            placeholder="Search field codes..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10 bg-stone-900 border-stone-800"
          />
        </div>
        <Select value={groupFilter} onValueChange={setGroupFilter}>
          <SelectTrigger className="w-48 bg-stone-900 border-stone-800">
            <SelectValue placeholder="Field Group" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Groups</SelectItem>
            {groups.map(g => (
              <SelectItem key={g} value={g}>{g.replace(/_/g, ' ')}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Field Groups */}
      {Object.entries(groupedMappings).map(([group, fields]) => (
        <div key={group} className="bg-stone-900 border border-stone-800 rounded-xl overflow-hidden">
          <div className="bg-stone-800/50 px-4 py-3 border-b border-stone-800">
            <h4 className="font-semibold text-white capitalize">{group.replace(/_/g, ' ')}</h4>
            <p className="text-xs text-slate-500">{fields.length} fields</p>
          </div>
          <div className="divide-y divide-stone-800">
            {fields.map(field => (
              <div key={field.id} className="px-4 py-3 hover:bg-stone-800/30">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <code className="bg-purple-500/10 text-purple-400 px-2 py-1 rounded text-sm font-mono">
                      {field.metro2_code_or_segment}
                    </code>
                    <span className="font-medium text-white">{field.display_name}</span>
                  </div>
                </div>
                <p className="text-sm text-slate-400 mt-1">{field.description}</p>
                {field.example_values?.length > 0 && (
                  <div className="flex items-center gap-2 mt-2">
                    <span className="text-xs text-slate-500">Examples:</span>
                    {field.example_values.slice(0, 3).map((v, i) => (
                      <Badge key={i} variant="outline" className="text-xs bg-stone-800 border-stone-700">
                        {v}
                      </Badge>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

// ============================================================================
// LEGAL AUTHORITIES TAB
// ============================================================================
const LegalAuthoritiesTab = ({ authorities, loading }) => {
  const [search, setSearch] = useState('');
  const [recipientFilter, setRecipientFilter] = useState('all');
  const [expandedId, setExpandedId] = useState(null);

  const filteredAuthorities = authorities.filter(a => {
    const matchesSearch = !search || 
      a.citation?.toLowerCase().includes(search.toLowerCase()) ||
      a.title?.toLowerCase().includes(search.toLowerCase());
    const matchesRecipient = recipientFilter === 'all' || a.recipient_type === recipientFilter;
    return matchesSearch && matchesRecipient;
  });

  if (loading) {
    return (
      <div className="space-y-4">
        {[...Array(5)].map((_, i) => (
          <Skeleton key={i} className="h-24 rounded-xl bg-stone-800" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="metro2-legal-authorities-tab">
      {/* Filters */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={16} />
          <Input
            placeholder="Search citations..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10 bg-stone-900 border-stone-800"
          />
        </div>
        <Select value={recipientFilter} onValueChange={setRecipientFilter}>
          <SelectTrigger className="w-40 bg-stone-900 border-stone-800">
            <SelectValue placeholder="Recipient" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Recipients</SelectItem>
            <SelectItem value="cra">CRA</SelectItem>
            <SelectItem value="furnisher">Furnisher</SelectItem>
            <SelectItem value="mixed">Mixed</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Authorities List */}
      <div className="space-y-3">
        {filteredAuthorities.map(auth => (
          <div
            key={auth.id}
            className="bg-stone-900 border border-stone-800 rounded-xl overflow-hidden"
          >
            <div
              className="p-4 cursor-pointer hover:bg-stone-800/30"
              onClick={() => setExpandedId(expandedId === auth.id ? null : auth.id)}
            >
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-blue-600/20 flex items-center justify-center">
                    <Scale className="text-blue-400" size={18} />
                  </div>
                  <div>
                    <h4 className="font-semibold text-white font-mono">{auth.citation}</h4>
                    <p className="text-sm text-slate-400">{auth.title}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="bg-blue-500/10 text-blue-400 border-blue-500/30">
                    {auth.recipient_type}
                  </Badge>
                  {expandedId === auth.id ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                </div>
              </div>
            </div>
            {expandedId === auth.id && (
              <div className="px-4 pb-4 pt-0 border-t border-stone-800 mt-2">
                <div className="space-y-3 mt-3">
                  <div>
                    <h5 className="text-xs text-slate-500 uppercase mb-1">Plain English Summary</h5>
                    <p className="text-sm text-slate-300">{auth.plain_english_summary}</p>
                  </div>
                  <div>
                    <h5 className="text-xs text-slate-500 uppercase mb-1">Letter Block (Short)</h5>
                    <p className="text-sm text-slate-400 italic">"{auth.letter_block_short}"</p>
                  </div>
                  {auth.no_overstatement_notes && (
                    <div className="bg-amber-500/5 border border-amber-500/20 rounded-lg p-3">
                      <h5 className="text-xs text-amber-400 uppercase mb-1">Compliance Note</h5>
                      <p className="text-sm text-amber-300/80">{auth.no_overstatement_notes}</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

// ============================================================================
// RED FLAGS TAB
// ============================================================================
const RedFlagsTab = ({ redFlags, loading }) => {
  const [search, setSearch] = useState('');
  const [severityFilter, setSeverityFilter] = useState('all');
  const [accountTypeFilter, setAccountTypeFilter] = useState('all');

  const accountTypes = [...new Set(redFlags.map(f => f.account_type))].filter(Boolean);

  const filteredFlags = redFlags.filter(f => {
    const matchesSearch = !search || 
      f.red_flag_name?.toLowerCase().includes(search.toLowerCase()) ||
      f.pattern?.toLowerCase().includes(search.toLowerCase());
    const matchesSeverity = severityFilter === 'all' || f.severity === severityFilter;
    const matchesType = accountTypeFilter === 'all' || f.account_type === accountTypeFilter;
    return matchesSearch && matchesSeverity && matchesType;
  });

  if (loading) {
    return (
      <div className="space-y-4">
        {[...Array(5)].map((_, i) => (
          <Skeleton key={i} className="h-20 rounded-xl bg-stone-800" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="metro2-red-flags-tab">
      {/* Filters */}
      <div className="flex items-center gap-4 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={16} />
          <Input
            placeholder="Search red flags..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10 bg-stone-900 border-stone-800"
          />
        </div>
        <Select value={severityFilter} onValueChange={setSeverityFilter}>
          <SelectTrigger className="w-36 bg-stone-900 border-stone-800">
            <SelectValue placeholder="Severity" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Severities</SelectItem>
            <SelectItem value="critical">Critical</SelectItem>
            <SelectItem value="high">High</SelectItem>
            <SelectItem value="medium">Medium</SelectItem>
            <SelectItem value="low">Low</SelectItem>
          </SelectContent>
        </Select>
        <Select value={accountTypeFilter} onValueChange={setAccountTypeFilter}>
          <SelectTrigger className="w-40 bg-stone-900 border-stone-800">
            <SelectValue placeholder="Account Type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            {accountTypes.map(t => (
              <SelectItem key={t} value={t}>{t}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Red Flags List */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {filteredFlags.map(flag => (
          <div
            key={flag.id}
            className="bg-stone-900 border border-stone-800 rounded-xl p-4 hover:border-red-500/30 transition-all"
          >
            <div className="flex items-start justify-between mb-2">
              <div className="flex items-center gap-2">
                <AlertTriangle className={`${flag.severity === 'critical' ? 'text-red-400' : flag.severity === 'high' ? 'text-orange-400' : 'text-amber-400'}`} size={18} />
                <h4 className="font-semibold text-white">{flag.red_flag_name}</h4>
              </div>
              <Badge variant="outline" className={SEVERITY_COLORS[flag.severity]}>
                {flag.severity}
              </Badge>
            </div>
            <p className="text-sm text-slate-400">{flag.pattern}</p>
            <div className="flex items-center gap-2 mt-3">
              <Badge variant="outline" className="text-xs bg-stone-800 border-stone-700">
                {flag.account_type}
              </Badge>
              {flag.evidence_needed && (
                <Badge variant="outline" className="text-xs bg-amber-500/10 text-amber-400 border-amber-500/30">
                  Evidence Required
                </Badge>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ============================================================================
// PLAYBOOKS TAB
// ============================================================================
const PlaybooksTab = ({ playbooks, loading }) => {
  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {[...Array(6)].map((_, i) => (
          <Skeleton key={i} className="h-32 rounded-xl bg-stone-800" />
        ))}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="metro2-playbooks-tab">
      {playbooks.map(pb => (
        <div
          key={pb.id}
          className="bg-stone-900 border border-stone-800 rounded-xl p-5 hover:border-amber-500/30 transition-all"
        >
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-lg bg-amber-600/20 flex items-center justify-center">
              <Book className="text-amber-400" size={18} />
            </div>
            <h4 className="font-semibold text-white">{pb.account_type}</h4>
          </div>
          <p className="text-sm text-slate-400">{pb.summary}</p>
        </div>
      ))}
    </div>
  );
};

// ============================================================================
// EVIDENCE TAB
// ============================================================================
const EvidenceTab = ({ evidence, loading }) => {
  const [accountTypeFilter, setAccountTypeFilter] = useState('all');
  
  const accountTypes = [...new Set(evidence.map(e => e.related_account_type))].filter(Boolean);

  const filteredEvidence = evidence.filter(e => 
    accountTypeFilter === 'all' || e.related_account_type === accountTypeFilter
  );

  if (loading) {
    return (
      <div className="space-y-4">
        {[...Array(5)].map((_, i) => (
          <Skeleton key={i} className="h-16 rounded-xl bg-stone-800" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="metro2-evidence-tab">
      {/* Filter */}
      <div className="flex items-center gap-4">
        <Select value={accountTypeFilter} onValueChange={setAccountTypeFilter}>
          <SelectTrigger className="w-48 bg-stone-900 border-stone-800">
            <SelectValue placeholder="Account Type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Account Types</SelectItem>
            {accountTypes.map(t => (
              <SelectItem key={t} value={t}>{t}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Evidence List */}
      <div className="space-y-3">
        {filteredEvidence.map(ev => (
          <div
            key={ev.id}
            className="bg-stone-900 border border-stone-800 rounded-xl p-4"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <FileCheck className="text-teal-400" size={18} />
                <div>
                  <h4 className="font-semibold text-white">{ev.evidence_name}</h4>
                  <p className="text-xs text-slate-500">{ev.related_account_type}</p>
                </div>
              </div>
              <Badge variant="outline" className={ev.required_or_optional === 'required' ? 'bg-red-500/10 text-red-400 border-red-500/30' : ev.required_or_optional === 'recommended' ? 'bg-amber-500/10 text-amber-400 border-amber-500/30' : 'bg-slate-500/10 text-slate-400 border-slate-500/30'}>
                {ev.required_or_optional}
              </Badge>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ============================================================================
// STRATEGIES TAB
// ============================================================================
const StrategiesTab = ({ strategies, loading }) => {
  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {[...Array(6)].map((_, i) => (
          <Skeleton key={i} className="h-28 rounded-xl bg-stone-800" />
        ))}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="metro2-strategies-tab">
      {strategies.map(strat => (
        <div
          key={strat.id}
          className="bg-stone-900 border border-stone-800 rounded-xl p-5 hover:border-indigo-500/30 transition-all"
        >
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-lg bg-indigo-600/20 flex items-center justify-center">
              <Mail className="text-indigo-400" size={18} />
            </div>
            <div>
              <h4 className="font-semibold text-white">{strat.strategy_name}</h4>
              <Badge variant="outline" className="text-xs bg-stone-800 border-stone-700 mt-1">
                {strat.tone}
              </Badge>
            </div>
          </div>
          <p className="text-sm text-slate-400">{strat.best_for}</p>
        </div>
      ))}
    </div>
  );
};

// ============================================================================
// RULE TESTING TAB - LIVE ACCOUNT TESTING
// ============================================================================
const RuleTestingTab = () => {
  // State for report/account selection
  const [reportSearch, setReportSearch] = useState('');
  const [reports, setReports] = useState([]);
  const [loadingReports, setLoadingReports] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);
  
  const [accounts, setAccounts] = useState([]);
  const [loadingAccounts, setLoadingAccounts] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);
  
  const [bureauScope, setBureauScope] = useState('all');
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [activeResultTab, setActiveResultTab] = useState('summary');

  // Search reports
  const searchReports = async (query = '') => {
    setLoadingReports(true);
    try {
      const response = await axios.get(`${API}/reports/search`, {
        params: { search: query, limit: 20 }
      });
      setReports(response.data.reports || []);
    } catch (error) {
      console.error('Failed to search reports:', error);
      toast.error('Failed to search reports');
    } finally {
      setLoadingReports(false);
    }
  };

  // Load accounts for selected report
  const loadAccounts = async (reportId) => {
    setLoadingAccounts(true);
    try {
      const response = await axios.get(`${API}/reports/${reportId}/accounts`, {
        params: { bureau: bureauScope }
      });
      setAccounts(response.data.accounts || []);
    } catch (error) {
      console.error('Failed to load accounts:', error);
      toast.error('Failed to load accounts');
    } finally {
      setLoadingAccounts(false);
    }
  };

  // Handle report selection
  const handleSelectReport = (report) => {
    setSelectedReport(report);
    setSelectedAccount(null);
    setTestResult(null);
    loadAccounts(report.report_id);
  };

  // Handle account selection
  const handleSelectAccount = (account) => {
    setSelectedAccount(account);
    setTestResult(null);
  };

  // Run live test
  const handleRunTest = async () => {
    if (!selectedReport || !selectedAccount) {
      toast.error('Please select a report and account');
      return;
    }
    
    setTesting(true);
    setTestResult(null);
    
    try {
      const response = await axios.post(`${API}/rules/test-live-account`, {
        report_id: selectedReport.report_id,
        account_id: selectedAccount.account_id,
        bureau: bureauScope,
        test_mode: 'single_account',
        include_payment_history: true,
        include_legal_authorities: true,
        include_field_mappings: true
      });
      setTestResult(response.data);
      setActiveResultTab('summary');
    } catch (error) {
      console.error('Test failed:', error);
      toast.error('Test failed: ' + (error.response?.data?.detail || error.message));
    } finally {
      setTesting(false);
    }
  };

  // Initial load
  useEffect(() => {
    searchReports();
  }, []);

  // Reload accounts when bureau changes
  useEffect(() => {
    if (selectedReport) {
      loadAccounts(selectedReport.report_id);
    }
  }, [bureauScope]);

  const ELIGIBILITY_BADGES = {
    'confirmed_conflict': { label: 'Confirmed Conflict', className: 'bg-red-500/10 text-red-400 border-red-500/30' },
    'metro2_review_needed': { label: 'Metro 2 Review Needed', className: 'bg-amber-500/10 text-amber-400 border-amber-500/30' },
    'verification_required': { label: 'Verification Required', className: 'bg-blue-500/10 text-blue-400 border-blue-500/30' },
    'evidence_needed': { label: 'Evidence Needed', className: 'bg-purple-500/10 text-purple-400 border-purple-500/30' },
    'factual_dispute_better': { label: 'Better for Factual Dispute', className: 'bg-slate-500/10 text-slate-400 border-slate-500/30' },
    'not_metro2_eligible': { label: 'Not Metro 2 Eligible', className: 'bg-stone-500/10 text-stone-400 border-stone-500/30' },
  };

  return (
    <div className="space-y-6" data-testid="metro2-live-testing-tab">
      {/* Report Selector */}
      <div className="bg-stone-900 border border-stone-800 rounded-xl p-6">
        <h3 className="font-semibold text-white mb-4 flex items-center gap-2">
          <Search size={18} /> Select Credit Report
        </h3>
        <div className="flex items-center gap-4 mb-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={16} />
            <Input
              placeholder="Search reports by user ID, report ID..."
              value={reportSearch}
              onChange={(e) => setReportSearch(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && searchReports(reportSearch)}
              className="pl-10 bg-stone-800 border-stone-700"
              data-testid="report-search-input"
            />
          </div>
          <Button 
            variant="outline" 
            onClick={() => searchReports(reportSearch)}
            className="border-stone-700"
          >
            <Search size={16} className="mr-2" /> Search
          </Button>
        </div>

        {loadingReports ? (
          <div className="space-y-2">
            {[...Array(3)].map((_, i) => (
              <Skeleton key={i} className="h-16 bg-stone-800" />
            ))}
          </div>
        ) : reports.length === 0 ? (
          <div className="text-center py-8 text-slate-500">
            <Database size={40} className="mx-auto mb-3 opacity-30" />
            <p>No imported reports available.</p>
            <p className="text-xs mt-1">Import a credit report first to use live testing.</p>
          </div>
        ) : (
          <div className="space-y-2 max-h-60 overflow-y-auto">
            {reports.map((report) => (
              <div
                key={report.report_id}
                onClick={() => handleSelectReport(report)}
                className={`p-3 rounded-lg border cursor-pointer transition-all ${
                  selectedReport?.report_id === report.report_id
                    ? 'bg-emerald-500/10 border-emerald-500/30'
                    : 'bg-stone-800/50 border-stone-700 hover:border-stone-600'
                }`}
                data-testid={`report-option-${report.report_id}`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-white text-sm">{report.report_id}</p>
                    <p className="text-xs text-slate-500">
                      {report.bureaus_found?.join(', ') || 'No bureaus'} | {report.total_accounts} accounts
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="text-xs bg-stone-700 border-stone-600">
                      {report.source}
                    </Badge>
                    {selectedReport?.report_id === report.report_id && (
                      <Check size={16} className="text-emerald-400" />
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Account Selector */}
      {selectedReport && (
        <div className="bg-stone-900 border border-stone-800 rounded-xl p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-white flex items-center gap-2">
              <List size={18} /> Select Account / Tradeline
            </h3>
            <Select value={bureauScope} onValueChange={setBureauScope}>
              <SelectTrigger className="w-40 bg-stone-800 border-stone-700" data-testid="bureau-scope-select">
                <SelectValue placeholder="Bureau" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Bureaus</SelectItem>
                <SelectItem value="experian">Experian</SelectItem>
                <SelectItem value="equifax">Equifax</SelectItem>
                <SelectItem value="transunion">TransUnion</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {loadingAccounts ? (
            <div className="space-y-2">
              {[...Array(3)].map((_, i) => (
                <Skeleton key={i} className="h-20 bg-stone-800" />
              ))}
            </div>
          ) : accounts.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <FileText size={40} className="mx-auto mb-3 opacity-30" />
              <p>No tradelines found for this report.</p>
            </div>
          ) : (
            <div className="space-y-2 max-h-80 overflow-y-auto">
              {accounts.map((account) => (
                <div
                  key={account.account_id}
                  onClick={() => handleSelectAccount(account)}
                  className={`p-4 rounded-lg border cursor-pointer transition-all ${
                    selectedAccount?.account_id === account.account_id
                      ? 'bg-emerald-500/10 border-emerald-500/30'
                      : 'bg-stone-800/50 border-stone-700 hover:border-stone-600'
                  }`}
                  data-testid={`account-option-${account.account_id}`}
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="font-medium text-white">{account.creditor_name}</p>
                      <p className="text-xs text-slate-500 mt-1">
                        {account.account_type} | {account.bureau.toUpperCase()}
                      </p>
                      <div className="flex items-center gap-2 mt-2">
                        <Badge variant="outline" className="text-xs bg-stone-700 border-stone-600">
                          {account.account_status || 'Unknown Status'}
                        </Badge>
                        {account.balance > 0 && (
                          <Badge variant="outline" className="text-xs bg-stone-700 border-stone-600">
                            ${account.balance?.toLocaleString()}
                          </Badge>
                        )}
                        {account.is_negative && (
                          <Badge variant="outline" className="text-xs bg-red-500/10 text-red-400 border-red-500/30">
                            Negative
                          </Badge>
                        )}
                        {account.is_collection && (
                          <Badge variant="outline" className="text-xs bg-orange-500/10 text-orange-400 border-orange-500/30">
                            Collection
                          </Badge>
                        )}
                        {account.is_chargeoff && (
                          <Badge variant="outline" className="text-xs bg-red-500/10 text-red-400 border-red-500/30">
                            Charge-Off
                          </Badge>
                        )}
                        {account.is_student_loan && (
                          <Badge variant="outline" className="text-xs bg-blue-500/10 text-blue-400 border-blue-500/30">
                            Student Loan
                          </Badge>
                        )}
                      </div>
                    </div>
                    {selectedAccount?.account_id === account.account_id && (
                      <Check size={18} className="text-emerald-400" />
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Run Test Button */}
          {selectedAccount && (
            <Button 
              onClick={handleRunTest}
              disabled={testing}
              className="mt-4 bg-emerald-600 hover:bg-emerald-700 w-full"
              data-testid="run-live-test-btn"
            >
              {testing ? (
                <>
                  <Loader2 className="animate-spin mr-2" size={16} />
                  Testing Against {reports.length} Rules...
                </>
              ) : (
                <>
                  <FlaskConical size={16} className="mr-2" />
                  Run Live Test Against Metro 2 Rules
                </>
              )}
            </Button>
          )}
        </div>
      )}

      {/* Test Results */}
      {testResult && (
        <div className="bg-stone-900 border border-stone-800 rounded-xl overflow-hidden" data-testid="live-test-results">
          {/* Results Header */}
          <div className="bg-stone-800/50 px-6 py-4 border-b border-stone-800">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-white">{testResult.creditor_name}</h3>
                <p className="text-sm text-slate-400">{testResult.consumer_friendly_summary}</p>
              </div>
              <div className="flex items-center gap-2">
                <Badge variant="outline" className={ELIGIBILITY_BADGES[testResult.eligibility_status]?.className || 'bg-stone-700'}>
                  {ELIGIBILITY_BADGES[testResult.eligibility_status]?.label || testResult.eligibility_status?.replace(/_/g, ' ')}
                </Badge>
                {testResult.generation_allowed ? (
                  <Badge variant="outline" className="bg-emerald-500/10 text-emerald-400 border-emerald-500/30">
                    <Check size={12} className="mr-1" /> Generation Allowed
                  </Badge>
                ) : (
                  <Badge variant="outline" className="bg-red-500/10 text-red-400 border-red-500/30">
                    <X size={12} className="mr-1" /> Generation Blocked
                  </Badge>
                )}
              </div>
            </div>
          </div>

          {/* Results Tabs */}
          <div className="border-b border-stone-800 px-2">
            <div className="flex gap-1 overflow-x-auto">
              {[
                { id: 'summary', label: 'Summary' },
                { id: 'rules', label: `Rules (${testResult.matched_rules?.length || 0})` },
                { id: 'red-flags', label: `Red Flags (${testResult.matched_red_flags?.length || 0})` },
                { id: 'fields', label: 'Field Mappings' },
                { id: 'legal', label: 'Legal Authorities' },
                { id: 'evidence', label: 'Evidence' },
                { id: 'debug', label: 'Debug' },
              ].map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveResultTab(tab.id)}
                  className={`px-4 py-3 text-sm font-medium whitespace-nowrap transition-all ${
                    activeResultTab === tab.id
                      ? 'text-emerald-400 border-b-2 border-emerald-400'
                      : 'text-slate-400 hover:text-white'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>

          {/* Results Content */}
          <div className="p-6">
            {activeResultTab === 'summary' && (
              <div className="space-y-6">
                {/* Stats Grid */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-stone-800 rounded-lg p-4">
                    <p className="text-xs text-slate-500 uppercase">Matched Rules</p>
                    <p className="text-2xl font-bold text-emerald-400">{testResult.matched_rules?.length || 0}</p>
                  </div>
                  <div className="bg-stone-800 rounded-lg p-4">
                    <p className="text-xs text-slate-500 uppercase">Red Flags</p>
                    <p className="text-2xl font-bold text-red-400">{testResult.matched_red_flags?.length || 0}</p>
                  </div>
                  <div className="bg-stone-800 rounded-lg p-4">
                    <p className="text-xs text-slate-500 uppercase">Confidence</p>
                    <p className="text-2xl font-bold text-blue-400">{Math.round((testResult.confidence_score || 0) * 100)}%</p>
                  </div>
                  <div className="bg-stone-800 rounded-lg p-4">
                    <p className="text-xs text-slate-500 uppercase">Route</p>
                    <p className="text-lg font-bold text-white">{testResult.recommended_route?.replace(/_/g, ' ')}</p>
                  </div>
                </div>

                {/* Key Info */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-stone-800/50 rounded-lg p-4">
                    <h4 className="text-sm text-slate-400 mb-2">Letter Tone</h4>
                    <Badge variant="outline" className="bg-stone-700 border-stone-600">
                      {testResult.letter_tone?.replace(/_/g, ' ')}
                    </Badge>
                  </div>
                  <div className="bg-stone-800/50 rounded-lg p-4">
                    <h4 className="text-sm text-slate-400 mb-2">Bureau Scope</h4>
                    <Badge variant="outline" className="bg-stone-700 border-stone-600">
                      {testResult.bureau_scope?.toUpperCase()}
                    </Badge>
                  </div>
                </div>

                {/* Generation Blockers */}
                {testResult.generation_blockers?.length > 0 && (
                  <div className="bg-red-500/5 border border-red-500/20 rounded-lg p-4">
                    <h4 className="text-sm text-red-400 font-semibold mb-2 flex items-center gap-2">
                      <AlertTriangle size={16} /> Generation Blockers
                    </h4>
                    <ul className="space-y-1 text-sm text-red-300/80">
                      {testResult.generation_blockers.map((blocker, i) => (
                        <li key={i}>• {blocker}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Payment History Analysis */}
                {testResult.monthly_history_breakdown?.has_issues && (
                  <div className="bg-amber-500/5 border border-amber-500/20 rounded-lg p-4">
                    <h4 className="text-sm text-amber-400 font-semibold mb-2">Payment History Issues</h4>
                    <div className="space-y-1">
                      {testResult.monthly_history_breakdown.issues?.map((issue, i) => (
                        <p key={i} className="text-sm text-amber-300/80">• {issue.description}</p>
                      ))}
                    </div>
                    {testResult.monthly_history_breakdown.pattern && (
                      <p className="text-xs text-slate-500 mt-2 font-mono">
                        Pattern: {testResult.monthly_history_breakdown.pattern}
                      </p>
                    )}
                  </div>
                )}

                {/* Account Type Reviews */}
                {testResult.collection_review?.is_collection && (
                  <div className="bg-orange-500/5 border border-orange-500/20 rounded-lg p-4">
                    <h4 className="text-sm text-orange-400 font-semibold mb-2">Collection Review</h4>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <span className="text-slate-400">Original Creditor:</span>
                      <span className="text-white">{testResult.collection_review.original_creditor || 'Missing'}</span>
                      <span className="text-slate-400">Balance:</span>
                      <span className="text-white">${testResult.collection_review.balance?.toLocaleString() || 0}</span>
                    </div>
                    {testResult.collection_review.original_creditor_missing && (
                      <Badge variant="outline" className="mt-2 bg-red-500/10 text-red-400 border-red-500/30">
                        Missing Original Creditor
                      </Badge>
                    )}
                  </div>
                )}

                {testResult.chargeoff_review?.is_chargeoff && (
                  <div className="bg-red-500/5 border border-red-500/20 rounded-lg p-4">
                    <h4 className="text-sm text-red-400 font-semibold mb-2">Charge-Off Review</h4>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <span className="text-slate-400">Balance After CO:</span>
                      <span className="text-white">${testResult.chargeoff_review.balance?.toLocaleString() || 0}</span>
                    </div>
                    {testResult.chargeoff_review.has_balance_after_chargeoff && (
                      <Badge variant="outline" className="mt-2 bg-red-500/10 text-red-400 border-red-500/30">
                        Balance Remains After Charge-Off
                      </Badge>
                    )}
                  </div>
                )}

                {testResult.student_loan_review?.is_student_loan && (
                  <div className="bg-blue-500/5 border border-blue-500/20 rounded-lg p-4">
                    <h4 className="text-sm text-blue-400 font-semibold mb-2">Student Loan Review</h4>
                    <p className="text-sm text-slate-300">Servicer: {testResult.student_loan_review.servicer}</p>
                  </div>
                )}
              </div>
            )}

            {activeResultTab === 'rules' && (
              <div className="space-y-3">
                {testResult.matched_rules?.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <Database size={40} className="mx-auto mb-3 opacity-30" />
                    <p>No matching Metro 2 Intelligence rules found for this account.</p>
                  </div>
                ) : (
                  testResult.matched_rules?.map((rule, i) => (
                    <div key={i} className="bg-stone-800/50 rounded-lg p-4">
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <h4 className="font-semibold text-white">{rule.rule_name}</h4>
                          <p className="text-xs text-slate-500 font-mono">{rule.rule_key}</p>
                        </div>
                        <Badge variant="outline" className="bg-emerald-500/10 text-emerald-400 border-emerald-500/30">
                          Score: {rule.match_score}
                        </Badge>
                      </div>
                      {rule.match_reasons?.length > 0 && (
                        <div className="mt-2">
                          <p className="text-xs text-slate-500 mb-1">Match Reasons:</p>
                          <ul className="space-y-1">
                            {rule.match_reasons.map((reason, j) => (
                              <li key={j} className="text-sm text-emerald-400/80">✓ {reason}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {rule.metro2_codes?.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-2">
                          {rule.metro2_codes.map((code, j) => (
                            <Badge key={j} variant="outline" className="text-xs bg-purple-500/10 text-purple-400 border-purple-500/30">
                              {code}
                            </Badge>
                          ))}
                        </div>
                      )}
                      {rule.wizard_short_explanation && (
                        <p className="text-sm text-slate-400 mt-2 italic">"{rule.wizard_short_explanation}"</p>
                      )}
                    </div>
                  ))
                )}
              </div>
            )}

            {activeResultTab === 'red-flags' && (
              <div className="space-y-3">
                {testResult.matched_red_flags?.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <AlertTriangle size={40} className="mx-auto mb-3 opacity-30" />
                    <p>No red flags detected for this account.</p>
                  </div>
                ) : (
                  testResult.matched_red_flags?.map((flag, i) => (
                    <div key={i} className="bg-stone-800/50 rounded-lg p-4">
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <AlertTriangle className={flag.severity === 'critical' ? 'text-red-400' : flag.severity === 'high' ? 'text-orange-400' : 'text-amber-400'} size={18} />
                          <h4 className="font-semibold text-white">{flag.red_flag_name}</h4>
                        </div>
                        <Badge variant="outline" className={SEVERITY_COLORS[flag.severity]}>
                          {flag.severity}
                        </Badge>
                      </div>
                      {flag.match_reasons?.length > 0 && (
                        <ul className="space-y-1 mt-2">
                          {flag.match_reasons.map((reason, j) => (
                            <li key={j} className="text-sm text-slate-400">• {reason}</li>
                          ))}
                        </ul>
                      )}
                      {flag.evidence_needed && (
                        <Badge variant="outline" className="mt-2 bg-amber-500/10 text-amber-400 border-amber-500/30">
                          Evidence Required
                        </Badge>
                      )}
                    </div>
                  ))
                )}
              </div>
            )}

            {activeResultTab === 'fields' && (
              <div className="space-y-3">
                {testResult.matched_field_mappings?.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <FileText size={40} className="mx-auto mb-3 opacity-30" />
                    <p>No field mappings matched.</p>
                  </div>
                ) : (
                  testResult.matched_field_mappings?.map((field, i) => (
                    <div key={i} className="bg-stone-800/50 rounded-lg p-4 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <code className="bg-purple-500/10 text-purple-400 px-2 py-1 rounded text-sm font-mono">
                          {field.metro2_code_or_segment}
                        </code>
                        <div>
                          <p className="text-white">{field.display_name}</p>
                          <p className="text-xs text-slate-500">{field.field_group?.replace(/_/g, ' ')}</p>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}

            {activeResultTab === 'legal' && (
              <div className="space-y-3">
                {testResult.matched_legal_authorities?.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <Scale size={40} className="mx-auto mb-3 opacity-30" />
                    <p>No legal authorities matched.</p>
                  </div>
                ) : (
                  testResult.matched_legal_authorities?.map((auth, i) => (
                    <div key={i} className="bg-stone-800/50 rounded-lg p-4">
                      <div className="flex items-center gap-3 mb-2">
                        <Scale className="text-blue-400" size={18} />
                        <div>
                          <p className="font-mono text-white">{auth.citation}</p>
                          <p className="text-sm text-slate-400">{auth.title}</p>
                        </div>
                      </div>
                      {auth.letter_block_short && (
                        <p className="text-sm text-slate-400 italic mt-2">"{auth.letter_block_short}"</p>
                      )}
                    </div>
                  ))
                )}
              </div>
            )}

            {activeResultTab === 'evidence' && (
              <div className="space-y-3">
                {testResult.evidence_needed?.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <FileCheck size={40} className="mx-auto mb-3 opacity-30" />
                    <p>No specific evidence requirements identified.</p>
                  </div>
                ) : (
                  testResult.evidence_needed?.map((ev, i) => (
                    <div key={i} className="bg-stone-800/50 rounded-lg p-4 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <FileCheck className="text-teal-400" size={18} />
                        <div>
                          <p className="text-white">{ev.evidence_name}</p>
                          <p className="text-xs text-slate-500">{ev.related_account_type}</p>
                        </div>
                      </div>
                      <Badge variant="outline" className={ev.required_or_optional === 'required' ? 'bg-red-500/10 text-red-400 border-red-500/30' : 'bg-amber-500/10 text-amber-400 border-amber-500/30'}>
                        {ev.required_or_optional}
                      </Badge>
                    </div>
                  ))
                )}
              </div>
            )}

            {activeResultTab === 'debug' && (
              <div className="space-y-4">
                <div className="bg-stone-800/50 rounded-lg p-4">
                  <h4 className="text-sm text-slate-400 mb-2">Account Flags</h4>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-sm">
                    {Object.entries(testResult.admin_debug_summary?.account_flags || {}).map(([key, val]) => (
                      <div key={key} className="flex items-center gap-2">
                        {val ? <Check size={14} className="text-emerald-400" /> : <X size={14} className="text-slate-500" />}
                        <span className="text-slate-400">{key.replace(/_/g, ' ')}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="bg-stone-800/50 rounded-lg p-4">
                  <h4 className="text-sm text-slate-400 mb-2">Rule Engine Stats</h4>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <span className="text-slate-400">Rules Checked:</span>
                    <span className="text-white">{testResult.admin_debug_summary?.rules_checked || 0}</span>
                    <span className="text-slate-400">Red Flags Checked:</span>
                    <span className="text-white">{testResult.admin_debug_summary?.red_flags_checked || 0}</span>
                  </div>
                </div>
                <div className="bg-stone-800/50 rounded-lg p-4">
                  <h4 className="text-sm text-slate-400 mb-2">Bureau Analysis</h4>
                  <p className="text-sm text-slate-300">
                    Status: {testResult.admin_debug_summary?.bureau_analysis?.status || 'N/A'}
                  </p>
                  <p className="text-sm text-slate-300">
                    Bureaus Found: {testResult.admin_debug_summary?.bureau_analysis?.bureaus_found?.join(', ') || 'N/A'}
                  </p>
                  {testResult.admin_debug_summary?.bureau_analysis?.differences?.length > 0 && (
                    <div className="mt-2">
                      <p className="text-xs text-amber-400">Differences Detected:</p>
                      {testResult.admin_debug_summary.bureau_analysis.differences.map((diff, i) => (
                        <p key={i} className="text-xs text-slate-400">
                          {diff.field}: {JSON.stringify(diff.values)}
                        </p>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================================================
// AI ASSISTANT TAB (Coming Soon)
// ============================================================================
const AIAssistantTab = () => {
  return (
    <div className="flex flex-col items-center justify-center py-16" data-testid="metro2-ai-assistant-tab">
      <div className="w-20 h-20 rounded-2xl bg-purple-600/20 flex items-center justify-center mb-6">
        <Sparkles className="text-purple-400" size={40} />
      </div>
      <h3 className="text-xl font-semibold text-white mb-2">AI Content Assistant</h3>
      <p className="text-slate-400 text-center max-w-md mb-6">
        AI-powered rule suggestions and content generation for Metro 2 technical disputes.
      </p>
      <Badge variant="outline" className="bg-purple-500/10 text-purple-400 border-purple-500/30 text-lg px-4 py-2">
        Coming Soon
      </Badge>
    </div>
  );
};

// ============================================================================
// AUDIT TAB
// ============================================================================
const AuditTab = ({ auditLogs, loading, onRefresh }) => {
  if (loading) {
    return (
      <div className="space-y-4">
        {[...Array(5)].map((_, i) => (
          <Skeleton key={i} className="h-16 rounded-xl bg-stone-800" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="metro2-audit-tab">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-white">Audit History</h3>
        <Button variant="outline" size="sm" onClick={onRefresh} className="border-stone-700">
          <RefreshCw size={14} className="mr-2" /> Refresh
        </Button>
      </div>
      
      {auditLogs.length === 0 ? (
        <div className="text-center py-12 text-slate-500">
          <History size={48} className="mx-auto mb-4 opacity-30" />
          <p>No audit logs yet</p>
        </div>
      ) : (
        <div className="space-y-3">
          {auditLogs.map(log => (
            <div
              key={log.id}
              className="bg-stone-900 border border-stone-800 rounded-xl p-4"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <History className="text-slate-500" size={16} />
                  <div>
                    <p className="text-white">
                      <span className="text-emerald-400">{log.action}</span> {log.resource_type}
                    </p>
                    <p className="text-xs text-slate-500">{log.resource_name}</p>
                  </div>
                </div>
                <span className="text-xs text-slate-500">
                  {log.timestamp ? new Date(log.timestamp).toLocaleString() : 'Unknown'}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ============================================================================
// RULE DETAIL DRAWER
// ============================================================================
const RuleDetailDrawer = ({ rule, open, onClose }) => {
  if (!rule) return null;

  return (
    <Sheet open={open} onOpenChange={onClose}>
      <SheetContent className="w-[500px] bg-stone-950 border-stone-800 overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="text-white">{rule.rule_name}</SheetTitle>
          <SheetDescription className="font-mono text-slate-500">{rule.rule_key}</SheetDescription>
        </SheetHeader>
        
        <div className="space-y-6 mt-6">
          {/* Status Badges */}
          <div className="flex items-center gap-2 flex-wrap">
            <Badge variant="outline" className={STATUS_COLORS[rule.status]}>
              {rule.status}
            </Badge>
            <Badge variant="outline" className={SEVERITY_COLORS[rule.severity]}>
              {rule.severity}
            </Badge>
            <Badge variant="outline" className={STATUS_COLORS[rule.approval_status]}>
              {rule.approval_status}
            </Badge>
          </div>

          {/* Description */}
          <div>
            <h4 className="text-sm text-slate-500 uppercase mb-2">Description</h4>
            <p className="text-slate-300">{rule.description}</p>
          </div>

          {/* Method Info */}
          <div className="bg-emerald-500/5 border border-emerald-500/20 rounded-lg p-4">
            <h4 className="text-sm text-emerald-400 uppercase mb-2">Method Enforcement</h4>
            <div className="grid grid-cols-2 gap-2 text-sm">
              <span className="text-slate-500">Family:</span>
              <code className="text-emerald-400">{rule.family}</code>
              <span className="text-slate-500">Method:</span>
              <code className="text-emerald-400">{rule.method}</code>
            </div>
          </div>

          {/* Metro 2 Codes */}
          {rule.metro2_codes?.length > 0 && (
            <div>
              <h4 className="text-sm text-slate-500 uppercase mb-2">Metro 2 Field Codes</h4>
              <div className="flex flex-wrap gap-2">
                {rule.metro2_codes.map((code, i) => (
                  <Badge key={i} variant="outline" className="bg-purple-500/10 text-purple-400 border-purple-500/30">
                    {code}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {/* Legal Basis */}
          {rule.legal_basis_mapping?.length > 0 && (
            <div>
              <h4 className="text-sm text-slate-500 uppercase mb-2">Legal Basis</h4>
              <div className="space-y-1">
                {rule.legal_basis_mapping.map((law, i) => (
                  <p key={i} className="text-sm text-blue-400 font-mono">{law}</p>
                ))}
              </div>
            </div>
          )}

          {/* Classification */}
          <div>
            <h4 className="text-sm text-slate-500 uppercase mb-2">Classification</h4>
            <div className="grid grid-cols-2 gap-2 text-sm">
              <span className="text-slate-500">Eligibility:</span>
              <span className="text-white">{rule.eligibility_status?.replace(/_/g, ' ')}</span>
              <span className="text-slate-500">Route:</span>
              <span className="text-white">{rule.recommended_route}</span>
              <span className="text-slate-500">Tone:</span>
              <span className="text-white">{rule.letter_tone}</span>
            </div>
          </div>

          {/* Consumer Explanation */}
          {rule.consumer_friendly_explanation && (
            <div>
              <h4 className="text-sm text-slate-500 uppercase mb-2">Consumer Explanation</h4>
              <p className="text-sm text-slate-300">{rule.consumer_friendly_explanation}</p>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================
const Metro2IntelligencePage = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  
  // Data state
  const [overview, setOverview] = useState(null);
  const [rules, setRules] = useState([]);
  const [fieldMappings, setFieldMappings] = useState([]);
  const [legalAuthorities, setLegalAuthorities] = useState([]);
  const [redFlags, setRedFlags] = useState([]);
  const [playbooks, setPlaybooks] = useState([]);
  const [evidence, setEvidence] = useState([]);
  const [strategies, setStrategies] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  
  // Rule detail drawer
  const [selectedRule, setSelectedRule] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [
        overviewRes,
        rulesRes,
        mappingsRes,
        authoritiesRes,
        flagsRes,
        playbooksRes,
        evidenceRes,
        strategiesRes,
        logsRes,
      ] = await Promise.all([
        axios.get(`${API}/overview`),
        axios.get(`${API}/rules`),
        axios.get(`${API}/field-mappings`),
        axios.get(`${API}/legal-authorities`),
        axios.get(`${API}/red-flags`),
        axios.get(`${API}/playbooks`),
        axios.get(`${API}/evidence`),
        axios.get(`${API}/strategies`),
        axios.get(`${API}/audit-logs?limit=50`),
      ]);

      setOverview(overviewRes.data.overview);
      setRules(rulesRes.data.rules || []);
      setFieldMappings(mappingsRes.data.field_mappings || []);
      setLegalAuthorities(authoritiesRes.data.legal_authorities || []);
      setRedFlags(flagsRes.data.red_flags || []);
      setPlaybooks(playbooksRes.data.playbooks || []);
      setEvidence(evidenceRes.data.evidence_requirements || []);
      setStrategies(strategiesRes.data.strategies || []);
      setAuditLogs(logsRes.data.audit_logs || []);
    } catch (error) {
      console.error('Failed to fetch Metro 2 Intelligence data:', error);
      toast.error('Failed to load Metro 2 Intelligence data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleViewRule = (rule) => {
    setSelectedRule(rule);
    setDrawerOpen(true);
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview':
        return <OverviewTab overview={overview} loading={loading} onRefresh={fetchData} />;
      case 'rules':
        return <RulesTab rules={rules} loading={loading} onViewRule={handleViewRule} onRefresh={fetchData} />;
      case 'field-mappings':
        return <FieldMappingsTab mappings={fieldMappings} loading={loading} />;
      case 'legal-authorities':
        return <LegalAuthoritiesTab authorities={legalAuthorities} loading={loading} />;
      case 'red-flags':
        return <RedFlagsTab redFlags={redFlags} loading={loading} />;
      case 'playbooks':
        return <PlaybooksTab playbooks={playbooks} loading={loading} />;
      case 'evidence':
        return <EvidenceTab evidence={evidence} loading={loading} />;
      case 'strategies':
        return <StrategiesTab strategies={strategies} loading={loading} />;
      case 'testing':
        return <RuleTestingTab />;
      case 'ai-assistant':
        return <AIAssistantTab />;
      case 'audit':
        return <AuditTab auditLogs={auditLogs} loading={loading} onRefresh={fetchData} />;
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-stone-950 text-white" data-testid="metro2-intelligence-page">
      {/* Header */}
      <div className="border-b border-stone-800 bg-stone-900/50">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-white flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-emerald-600/20 flex items-center justify-center">
                  <Database className="text-emerald-400" size={22} />
                </div>
                Metro 2 Intelligence
              </h1>
              <p className="text-slate-400 mt-1">Rule Library, Field Mappings, and Legal Authorities</p>
            </div>
            <div className="flex items-center gap-3">
              <Button variant="outline" className="border-stone-700" onClick={fetchData}>
                <RefreshCw size={16} className="mr-2" /> Refresh
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-stone-800 bg-stone-900/30 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center gap-1 overflow-x-auto py-1">
            {TABS.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-medium rounded-lg transition-all whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/30'
                    : 'text-slate-400 hover:text-white hover:bg-stone-800'
                }`}
                data-testid={`tab-${tab.id}`}
              >
                <tab.icon size={16} />
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-6 py-6">
        {renderTabContent()}
      </div>

      {/* Rule Detail Drawer */}
      <RuleDetailDrawer
        rule={selectedRule}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
      />
    </div>
  );
};

export default Metro2IntelligencePage;
